import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { Briefcase, Eye, EyeOff } from 'lucide-react';

const BusinessRegister = () => {
  const [formData, setFormData] = useState({
    // User account fields
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    // Business info fields
    businessName: '',
    taxId: '',
    businessLicense: '',
    website: '',
    description: '',
    // Store location fields
    storeName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    storeHours: '',
    pickupInstructions: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    if (!formData.businessName || !formData.street || !formData.city || !formData.state || !formData.zipCode || !formData.phone) {
      setError('Please fill in all required business information fields');
      return;
    }

    setLoading(true);

    // Register user with business account application
    const result = await register({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      businessInfo: {
        businessName: formData.businessName,
        taxId: formData.taxId,
        businessLicense: formData.businessLicense,
        website: formData.website,
        description: formData.description,
      },
      storeLocation: {
        storeName: formData.storeName || formData.businessName,
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        phone: formData.phone,
        storeHours: formData.storeHours,
        pickupInstructions: formData.pickupInstructions,
      },
      applyForBusiness: true,
    });

    if (result.success) {
      navigate('/business-application-submitted');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="container" style={{ maxWidth: '680px', paddingTop: '60px', paddingBottom: '60px' }}>
      <div className="card">
        <div className="text-center mb-3">
          <Briefcase size={48} color="var(--primary)" />
          <h1 style={{ marginTop: '16px', marginBottom: '8px' }}>Business Account Registration</h1>
          <p className="text-muted">Create your account and apply for a business account</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Personal Information</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="firstName" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                First Name *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>

            <div>
              <label htmlFor="lastName" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Last Name *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="username" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Username *
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '48px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--textSecondary)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Confirm Password *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '48px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--textSecondary)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Business Information */}
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            Business Information
          </h3>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="businessName" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Business Name *
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="taxId" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Tax ID (Optional)
              </label>
              <input
                type="text"
                id="taxId"
                name="taxId"
                value={formData.taxId}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>

            <div>
              <label htmlFor="businessLicense" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Business License (Optional)
              </label>
              <input
                type="text"
                id="businessLicense"
                name="businessLicense"
                value={formData.businessLicense}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="website" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Website (Optional)
            </label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder="https://www.example.com"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Business Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Store Location */}
          <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            Store Location
          </h3>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="street" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Street Address *
            </label>
            <input
              type="text"
              id="street"
              name="street"
              value={formData.street}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="city" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                City *
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>

            <div>
              <label htmlFor="state" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                State *
              </label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                maxLength={2}
                placeholder="CA"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="zipCode" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                ZIP Code *
              </label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>

            <div>
              <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '16px',
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="storeHours" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Store Hours (Optional)
            </label>
            <input
              type="text"
              id="storeHours"
              name="storeHours"
              value={formData.storeHours}
              onChange={handleChange}
              placeholder="Mon-Fri: 9AM-5PM"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="pickupInstructions" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
              Pickup Instructions (Optional)
            </label>
            <textarea
              id="pickupInstructions"
              name="pickupInstructions"
              value={formData.pickupInstructions}
              onChange={handleChange}
              rows={2}
              placeholder="Special instructions for customer pickup"
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '16px',
                resize: 'vertical',
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%', marginBottom: '16px' }}
          >
            {loading ? 'Submitting application...' : 'Submit Business Account Application'}
          </button>

          <p className="text-center text-muted" style={{ fontSize: '13px', marginBottom: '12px', lineHeight: 1.5 }}>
            By creating a business account, you agree to our{' '}
            <Link to="/terms" style={{ color: 'var(--primary)', fontWeight: '600' }}>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" style={{ color: 'var(--primary)', fontWeight: '600' }}>
              Privacy Policy
            </Link>
            .
          </p>

          <div className="text-center text-muted">
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--primary)', fontWeight: '600' }}>
              Log in
            </Link>
            {' or '}
            <Link to="/register" style={{ color: 'var(--primary)', fontWeight: '600' }}>
              Sign up as individual
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessRegister;
